import { SharedValueContext } from '../shared-values.model';
import { useContext, useMemo } from 'react';
import { firstValueFrom } from 'rxjs';
import { SharedCollectionContext } from './shared-collection-context';
import { SharedStoreEventsContext } from './events-context';
import { SharedValueFunctionContext } from './shared-value-function-context';
import { getSharedValue } from '../shared-store-reader';
import mapValues from 'lodash.mapvalues';
import { produce } from 'immer';
import { unstable_batchedUpdates } from 'react-dom';
import { waitForNext } from '../../operators';
export function useSharedValueStoreContext() {
    var store = useContext(SharedValueContext);
    return useMemo(function () { return new SharedValueStoreContext(store); }, [store]);
}
var SharedValueStoreContext = /** @class */ (function () {
    function SharedValueStoreContext(store) {
        this.store = store;
        this.collection = new SharedCollectionContext(store);
        this.funcs = new SharedValueFunctionContext(this);
        this.events = new SharedStoreEventsContext(this);
    }
    SharedValueStoreContext.prototype.batch = function (action) {
        unstable_batchedUpdates(action);
    };
    SharedValueStoreContext.prototype.get = function (key, valueKeyOrKeys) {
        if (isValueFunction(key)) {
            return this.funcs.get(key);
        }
        else if (isCollectionKey(key)) {
            return this.collection.get(key, valueKeyOrKeys);
        }
        else
            return getSharedValue(this.store, key);
    };
    /*eslint-enable no-dupe-class-members*/
    SharedValueStoreContext.prototype.getAll = function (key) {
        return this.collection.getAll(key);
    };
    SharedValueStoreContext.prototype.waitFor = function (key, valueKeyOrCriteria, criteria) {
        if (isValueFunction(key)) {
            return this.funcs.waitFor(key, valueKeyOrCriteria);
        }
        else if (isCollectionKey(key)) {
            var valueKey = valueKeyOrCriteria;
            return this.collection.waitFor(key, valueKey, criteria);
        }
        else {
            return this.waitForValue(key, valueKeyOrCriteria);
        }
    };
    SharedValueStoreContext.prototype.waitForNext = function (event, filter) {
        return this.events.waitForNext(event, filter);
    };
    SharedValueStoreContext.prototype.on = function (event, listener) {
        return this.events.on(event, listener);
    };
    SharedValueStoreContext.prototype.stream = function (event) {
        return this.events.stream(event);
    };
    SharedValueStoreContext.prototype.dispatch = function (event, payload) {
        return this.events.dispatch(event, payload);
    };
    SharedValueStoreContext.prototype.changes = function (key, valueKeyOrKeys) {
        if (isValueFunction(key)) {
            return this.funcs.changes(key);
        }
        else if (isCollectionKey(key)) {
            return this.collection.changes(key, valueKeyOrKeys);
        }
        else
            return this.store.onChange(key.id);
    };
    /*eslint-enable no-dupe-class-members*/
    SharedValueStoreContext.prototype.anyChanges = function (key) {
        if (key)
            return this.collection.anyChanges(key);
        else
            return this.store.onAnyChange();
    };
    SharedValueStoreContext.prototype.set = function (key, valueKeyOrValue, value) {
        if (isCollectionKey(key)) {
            var valueKey = valueKeyOrValue;
            return this.collection.set(key, valueKey, value);
        }
        else {
            this.store.setValue(key.id, valueKeyOrValue);
        }
    };
    SharedValueStoreContext.prototype.modify = function (key, valueKeyOrModify, modify) {
        if (isCollectionKey(key)) {
            this.collection.modify(key, valueKeyOrModify, modify);
        }
        else {
            var current = this.get(key);
            var newValue = produce(current, valueKeyOrModify);
            this.set(key, newValue);
        }
    };
    /*eslint-enable no-dupe-class-members*/
    SharedValueStoreContext.prototype.modifyAll = function (key, modify) {
        this.collection.modifyAll(key, modify);
    };
    SharedValueStoreContext.prototype.update = function (key, update) {
        return this.collection.update(key, update);
    };
    SharedValueStoreContext.prototype.delete = function (key, valueKey) {
        this.collection.delete(key, valueKey);
    };
    SharedValueStoreContext.prototype.clear = function (key) {
        this.collection.clear(key);
    };
    SharedValueStoreContext.prototype.service = function (factory) {
        var service = factory(this);
        return mapValues(service, wrapServiceFunction);
    };
    SharedValueStoreContext.prototype.waitForValue = function (key, criteria) {
        var value = getSharedValue(this.store, key);
        if (valueMeetsCriteria(value, criteria))
            return Promise.resolve(value);
        else
            return waitForUpdate(this.store, key.id, criteria);
    };
    return SharedValueStoreContext;
}());
export { SharedValueStoreContext };
export function valueMeetsCriteria(value, criteria) {
    if (!criteria)
        return !!value;
    else
        return criteria(value);
}
export function waitForUpdate(store, key, criteria) {
    return firstValueFrom(store.onChange(key).pipe(waitForNext(criteria)));
}
function isCollectionKey(key) {
    return key['collection'] === true;
}
function isValueFunction(key) {
    return typeof key === 'function';
}
function wrapServiceFunction(serviceFunc) {
    return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var returnValue;
        unstable_batchedUpdates(function () {
            returnValue = serviceFunc.apply(void 0, args);
        });
        return returnValue;
    };
}
//# sourceMappingURL=shared-values-context.js.map